import React, { useEffect } from "react";
import { Button, Grid, Paper, TableCell, Tooltip, Typography } from "@mui/material";
import CatalogManagerTable from "../../../../../components/CatalogManagerTable";
import TableRow from "@mui/material/TableRow";
import CatalogFilters, { addFiltersToTable } from "../../../../../components/CatalogFilters/catalog_filters";
import SettingsTableMoreOpts from "./taxonomy_table_more_opts";
import { SavedSearchManager } from "../../../../../components/saved_search";
import { getTableConfig } from '../../../../../components/saved_search/utils/getConfig';
import { applySavedSearchConfig } from '../../../../../components/saved_search/utils/applyConfig';
import AttributeDialog from "./Dialogs/AttributeDalog/AttributeDialog";
import CategoryDialog from "./Dialogs/CategoryDialog";
import AttributeSetDialog from "./Dialogs/AttributeSetDialog/AttributeSetDialog";
import { getAttributes as utilGetAttributes, useDidUpdateEffect, utilDeleteAll } from "../../../../../utils/utils";
import EnhancedErrorDialog from "../../../../../components/EnhancedErrorDialog";
import { AddCircleOutline, Delete, PriorityHigh } from "@mui/icons-material";
import { Alert } from '@mui/material';
import Auth from "../../../../../auth";
import { ExportIcon, ImportIcon } from "../../../../../utils/icons";
import EnhancedConfirmDialog from "../../../../../components/EnhancedConfirmDialog";
import UpdatingDialog from "../../../../../components/Dialogs/UpdatingDialog";
import { tableRowSx, tableCellTruncateSx, pageContentGap } from "../../../../../theme";

interface ErrorState {
    open?: boolean;
    errorHeader?: string;
    errorMessage?: string;
    type?: string;
    [key: string]: any;
}

interface AttributeDialogControl {
    open: boolean;
    isEdit: boolean;
    previousAttribute: any;
}

interface CategoryDialogControl {
    open: boolean;
    isEdit: boolean;
    isParent?: boolean;
    previousCategory: any;
    type?: string;
}

interface AttributeSetDialogControl {
    open: boolean;
    isEdit: boolean;
    previousAttributeSet: any;
}

interface TaxonomyResultsProps {
    currentSetting: string;
    table: any;
    config: any;
    setConfig: (updater: (prevState: any) => any) => void;
    history: any;
    location: any;
    cancelSource: { cancel: (reason?: string) => void };
    resetCancelSource: () => void;
    condensed?: boolean;
    filterShowExceptions?: string[];
}

function TaxonomyResults(props: TaxonomyResultsProps) {
    const hasSettingsManagePermission = Auth.hasPermission("settings/manage");

    const [attributeDialogControl, setAttributeDialogControl] = React.useState<AttributeDialogControl>({
        open: false, isEdit: false, previousAttribute: null
    });
    const [categoryDialogControl, setCategoryDialogControl] = React.useState<CategoryDialogControl>({
        open: false, isEdit: false, isParent: false, previousCategory: null
    });
    const [attributeSetDialogControl, setAttributeSetDialogControl] = React.useState<AttributeSetDialogControl>({
        open: false, isEdit: false, previousAttributeSet: null
    });

    const [availableAttributes, setAvailableAttributes] = React.useState<any[]>([]);
    const [error, setError] = React.useState<ErrorState>({ open: false, errorHeader: "", errorMessage: "" });
    const [forceRefresh, setForceRefresh] = React.useState(false);
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    const [deletingAll, setDeletingAll] = React.useState(false);

    //region Functions
    const AddNewItem = () => {
        if (props.currentSetting === "attributes")
            return (
                <Button variant="outlined"
                    data-testid="poc-taxonomy-add-attribute"
                    style={{ width: "250px" }}
                    onClick={() => {
                        setAttributeDialogControl((prevState) => ({
                            ...prevState,
                            open: true,
                            isEdit: false,
                            previousAttribute: null
                        }));
                    }}
                >
                    <AddCircleOutline style={{ marginRight: "10px" }} />
                    <Typography>Add new Attribute</Typography>
                </Button>
            )
        else if (props.currentSetting === "attributeSets")
            return (
                <Button variant="outlined"
                    data-testid="poc-taxonomy-add-attribute-set"
                    style={{ width: "250px" }}
                    onClick={() => {
                        setAttributeSetDialogControl((prevState) => ({
                            ...prevState,
                            open: true,
                            isEdit: false,
                            previousAttributeSet: null
                        }));
                    }}
                >
                    <AddCircleOutline style={{ marginRight: "10px" }} />
                    <Typography>Add new Attribute Set</Typography>
                </Button>
            )
        else
            return (
                <Button variant="outlined"
                    data-testid="poc-taxonomy-add-category"
                    style={{ width: "250px" }}
                    onClick={() => {
                        setCategoryDialogControl((prevState) => ({
                            ...prevState,
                            open: true,
                            isEdit: false,
                            previousCategory: null,
                            isParent: true
                        }));
                    }}
                >
                    <AddCircleOutline style={{ marginRight: "10px" }} />
                    <Typography>Add new Category</Typography>
                </Button>
            )
    }
    const generateRows = () => {
        if (props.table.state.loading) return <></>;

        return props.table.data.map((row: any, rowIndex: number) => {
            let hasErrors = false;
            if (props.config.title === "attributeSets") {
                if (row.Attributes === undefined) {
                    hasErrors = true;
                } else {
                    let containsValidAttributes = (arr: any[], target: any[]) => target.every(v => arr.includes(v));
                    hasErrors = containsValidAttributes(availableAttributes, row.Attributes) === false;
                }
            }

            return (
                <TableRow
                    key={rowIndex}
                    data-testid={`poc-taxonomy-row-${props.config.title}-${row.Name || row.Id}`}
                    sx={tableRowSx}
                    style={{ height: "70px", cursor: "pointer" }}
                    onClick={() => {
                        if (props.config.title === "attributes") setAttributeDialogControl({
                            open: true,
                            isEdit: true,
                            previousAttribute: row
                        });
                        else if (props.config.title === "categories") setCategoryDialogControl({
                            open: true,
                            isEdit: true,
                            isParent: row.Level === 0,
                            previousCategory: row
                        });
                        else if (props.config.title === "attributeSets") setAttributeSetDialogControl({
                            open: true,
                            isEdit: true,
                            previousAttributeSet: row
                        });
                    }}
                >
                    {props.config.columns.map((column: any, colIndex: number) => {
                        if (props.table.extensions.isColumnHidden(column)) return null;
                        if (column.hidden && !column.toggleable) return null;

                        let content = column.template
                            ? column.template(row, column, colIndex, rowIndex,
                                (filter: any) => addFiltersToTable([filter], props.table))
                            : (<>{`${row[column.id] === undefined ? "" : row[column.id]}`}</>);

                        return (
                            <TableCell key={`${rowIndex}-${colIndex}`}
                                style={{
                                    width: column.width,
                                    minWidth: column.width,
                                    maxWidth: column.width,
                                }}
                            >
                                {content}
                            </TableCell>
                        )
                    })}
                    <TableCell sx={tableCellTruncateSx} style={{ width: 0, padding: "4px 0" }}>
                        <SettingsTableMoreOpts
                            setConfig={props.setConfig}
                            currentSetting={props.config.title}
                            row={row}
                            attributeDialog={attributeDialogControl} setAttributeDialog={setAttributeDialogControl}
                            categoryDialog={categoryDialogControl} setCategoryDialog={setCategoryDialogControl}
                            attributeSetDialog={attributeSetDialogControl}
                            setAttributeSetDialog={setAttributeSetDialogControl}
                        />
                    </TableCell>
                    <TableCell sx={tableCellTruncateSx} style={{ width: 0, padding: "4px 20px 4px 0" }}>
                        {hasErrors && (
                            <Grid container justifyContent={"center"} alignItems={"center"}>
                                <Tooltip title={"Attribute set contains missing attributes"}>
                                    <PriorityHigh style={{ color: "#f44336" }} />
                                </Tooltip>
                            </Grid>
                        )}
                    </TableCell>
                </TableRow>
            );
        });
    }
    const deleteAll = async (type: string) => {
        setDeletingAll(true);
        const response = await utilDeleteAll(type);
        if (response.open) setError(response);
        else {
            await new Promise(resolve => setTimeout(resolve, 1000));
            props.table.extensions.getData(undefined, true);
        }
        setDeletingAll(false);
    }
    //endregion

    // region Lifecycle
    /* Required to have this logic, otherwise the table will not update when switching between settings */
    /* This is because if the config filter settings (orderBy, order) are changed and filterInUrl is set to true
       then for some reason it doesn't get any new data, so we have to force it to get new data. */
    /* We also set up a cancel source to cancel previous API calls if the settings are changed. */
    useDidUpdateEffect(() => {
        if (props.table.state.loading) {
            props.cancelSource.cancel("Canceled due to new settings load.");
            props.resetCancelSource();
        }
        props.table.extensions.getData(undefined, true);
    }, [props.currentSetting]);

    useEffect(() => {
        const getAttributes = async () => {
            const attributes = await utilGetAttributes(undefined, true);
            if (attributes.errorMessage) setError(attributes);
            else setAvailableAttributes(attributes.map((attribute: any) => attribute.Name));
        }

        // Attributes need to be pulled to check if a set contains invalid attributes //
        if (props.config.title === "attributeSets") getAttributes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.config, props.currentSetting]);

    useEffect(() => {
        setForceRefresh(!forceRefresh);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableAttributes]);
    //endregion

    return (
        <>
            <Grid style={{
                position: "absolute",
                right: "14px",
                display: "flex",
                gap: pageContentGap
            }}>
                {hasSettingsManagePermission && (
                    <Button variant={"outlined"} style={{ color: "#f44336", borderColor: "#f44336" }}
                        onClick={() => {
                            setConfirmDelete(true);
                        }}
                    >
                        <Delete style={{ marginRight: "10px" }} />
                        Delete All {props.currentSetting === "categories" ? "Categories" : props.currentSetting === "attributes" ? "Attributes" : "Attribute Sets"}
                    </Button>
                )}
                {hasSettingsManagePermission && (
                    <Button
                        style={{ gap: pageContentGap }}
                        onClick={() => {
                            if (props.currentSetting === "categories") {
                                props.history.push("/productonboardingcenter/import/categories");
                            } else if (props.currentSetting === "attributes") {
                                props.history.push("/productonboardingcenter/import/attributes");
                            } else {
                                props.history.push("/productonboardingcenter/import/attributeSets");
                            }
                        }}
                    >
                        <ImportIcon className={`icons`} />
                        Import
                    </Button>
                )}
                <Button
                    style={{ gap: pageContentGap }}
                    onClick={() => {
                        if (props.currentSetting === "categories") {
                            props.history.push("/productonboardingcenter/export/categories");
                        } else if (props.currentSetting === "attributes") {
                            props.history.push("/productonboardingcenter/export/attributes");
                        } else {
                            props.history.push("/productonboardingcenter/export/attributeSets");
                        }
                    }}
                >
                    <ExportIcon className={`icons`} />
                    Export
                </Button>

                {hasSettingsManagePermission && (
                    <AddNewItem />
                )}
            </Grid>

            <Paper style={{ width: "100%" }}>
                <Alert severity={"info"} style={{}}>
                    <Typography variant={"body1"}>
                        {props.currentSetting === "attributes" && (
                            <Typography style={{ textAlign: "left" }}>
                                Attributes allow retailers to define the different fields they would like to collect
                                from suppliers to help describe a product.
                            </Typography>
                        )}

                        {props.currentSetting === "attributeSets" && (
                            <Typography style={{ textAlign: "left" }}>
                                Attribute sets are groups of attributes used to create templates for information
                                collected from
                                the supplier. Attribute sets are also used to organize the way the data is displayed on
                                the
                                product detail page.
                            </Typography>
                        )}


                        {props.currentSetting === "categories" && (
                            <Typography style={{ textAlign: "left" }}>
                                Categories are used to organize products with similar characteristics and uses.
                                Categories may
                                have children and siblings (also known as levels) with different attribute sets to help
                                create a
                                hierarchical structure that is more organized.
                            </Typography>
                        )}
                    </Typography>
                </Alert>
            </Paper>

            {/* Saved Search */}
            <Paper style={{ width: "100%", padding: '10px 12px', paddingLeft: pageContentGap, paddingRight: pageContentGap, boxSizing: 'border-box' }}>
                <SavedSearchManager
                    key={`saved-search-${props.currentSetting}`}
                    tableType={`taxonomy-${props.currentSetting}`}
                    tableState={getTableConfig(props.table, props.filterShowExceptions, props.config)}
                    initialFilters={props.config.filter}
                    variant={props.condensed ? 'condensed' : 'standard'}
                    onConfigApplied={(config) => {
                        applySavedSearchConfig(config as any, {
                            table: props.table,
                            columns: props.config.columns,
                            setConfig: props.setConfig
                        });
                    }}
                />
            </Paper>

            <Paper style={{ width: "100%" }}>
                <Grid container>
                    <CatalogFilters
                        table={props.table}
                        config={props.config} setConfig={props.setConfig}
                        history={props.history} location={props.location}
                    />
                </Grid>
            </Paper>
            {/*endregion*/}

            {/*region Table */}
            <Paper style={{ width: "100%" }}>
                <Grid container style={{ overflow: "auto", flexDirection: "column" }}>
                    <CatalogManagerTable
                        forceRefresh={forceRefresh}
                        table={props.table}
                        config={props.config}
                        generateRows={generateRows}
                    />
                </Grid>
            </Paper>
            {/*endregion*/}

            {/*region Dialogs*/}
            <EnhancedConfirmDialog
                open={confirmDelete}
                setOpen={setConfirmDelete}
                action={(action: boolean) => {
                    const type = props.currentSetting === "categories" ? "Categories" : props.currentSetting === "attributes" ? "Attributes" : "AttributeSets";
                    if (action) deleteAll(type);
                }}
                type={"delete"}
                title={`Delete All 
                    ${props.currentSetting === "categories" ? "Categories" : props.currentSetting === "attributes" ? "Attributes" : "Attribute Sets"}
                    from Taxonomy?
                `}
                message={`
                    Deleting all items is permanent and cannot be undone. Ensure you have backed up any necessary data before proceeding.
                `}
            />

            <UpdatingDialog
                open={deletingAll}
                setOpen={setDeletingAll}
                title={"Deleting all items..."}
                subtitle={"Please wait while we delete all items."}
            />

            <AttributeDialog
                config={props.config}
                setConfig={props.setConfig}

                history={props.history}
                location={props.location}

                open={attributeDialogControl.open}
                setOpen={((e: boolean) => setAttributeDialogControl((prevState) => ({ ...prevState, open: e })))}

                previousAttribute={attributeDialogControl.previousAttribute}
                isEdit={attributeDialogControl.isEdit}
            />

            <CategoryDialog
                config={props.config}
                setConfig={props.setConfig}

                history={props.history}
                location={props.location}

                open={categoryDialogControl.open}
                setOpen={((e: boolean) => setCategoryDialogControl((prevState) => ({ ...prevState, open: e })))}

                previousCategory={categoryDialogControl.previousCategory}
                isEdit={categoryDialogControl.isEdit}
                type={categoryDialogControl.type}
                isParent={categoryDialogControl.isParent}
            />

            <AttributeSetDialog
                config={props.config}
                setConfig={props.setConfig}

                history={props.history}
                location={props.location}

                open={attributeSetDialogControl.open}
                setOpen={((e: boolean) => setAttributeSetDialogControl((prevState) => ({ ...prevState, open: e })))}

                previousAttributeSet={attributeSetDialogControl.previousAttributeSet}
                isEdit={attributeSetDialogControl.isEdit}
            />
            {/*endregion*/}

            <EnhancedErrorDialog open={error.open}
                onClose={() => {
                    if (error.type === "permissions")
                        if (props.location.key === undefined)
                            props.history.push("/productonboardingcenter/catalog?catalog=retailer");
                        else
                            props.history.goBack();
                    else {
                        setError({ ...error, open: false })
                    }
                }}
                errorHeader={error.errorHeader} errorMessage={error.errorMessage} />
        </>
    );
}

export default TaxonomyResults;
